/**
 * Bildirim ve onay kutuları — SweetAlert2 sarmalayıcısı.
 *
 * Neden sarmalayıcı: SweetAlert2 kendi renklerini ve yazı tipini getirir.
 * Doğrudan çağrılırsa panelin tasarım sistemiyle çakışır (docs/13 bölüm 6:
 * arayüz kendi rengini seçmez). Burada her çağrı token'lara bağlanır ve
 * pencerenin biçimi tek yerden yönetilir.
 *
 * Görev dağılımı:
 *   - **onayIste** → geri alınamaz bir işlemden ÖNCE. Fatura göndermek para ve
 *     yasal sonuç doğurur; yanlış tıklama kabul edilemez.
 *   - **basariBildir / hataBildir** → işlemden SONRA, köşede kısa bildirim.
 *     Akışı kesmez.
 *   - **hataGoster** → ekranı kesen hata penceresi; yalnızca kullanıcının
 *     mutlaka okuması gereken durumlarda (yetki yok, oturum bitti).
 *
 * Ham hata mesajı ASLA gösterilmez; sunucunun hazırladığı Türkçe metin
 * gösterilir (src/errors/presentation.ts).
 */

import Swal, { type SweetAlertOptions } from 'sweetalert2';
import { ApiHatasi } from './api';

/** Geri alınamaz işlemin onay düğmesi — eylem olduğu için durum rengi değil. */
const TEHLIKE_DUGMESI =
  'inline-flex items-center rounded-[var(--radius-sm)] border border-[var(--state-bad)] bg-[var(--state-bad)] px-3 py-2 text-sm font-medium text-[var(--surface)]';

/** Tüm pencerelerde ortak biçim — tasarım token'larına bağlanır. */
const ORTAK: SweetAlertOptions = {
  buttonsStyling: false,
  // Yarıçap: pencere bir "uzuv"dur → --radius-lg; düğmeler parmak → --radius-sm.
  customClass: {
    popup:
      'rounded-[var(--radius-lg)] border border-[var(--line)] bg-[var(--surface)] text-[var(--ink)] font-sans',
    title: 'text-lg font-semibold text-[var(--ink)]',
    htmlContainer: 'text-sm text-[var(--ink-muted)]',
    actions: 'gap-2',
    confirmButton:
      'inline-flex items-center rounded-[var(--radius-sm)] bg-[var(--accent)] px-3 py-2 text-sm font-medium text-[var(--accent-ink)] hover:bg-[var(--accent-hover)]',
    denyButton:
      'inline-flex items-center rounded-[var(--radius-sm)] border border-[var(--state-bad)] bg-[var(--state-bad-soft)] px-3 py-2 text-sm font-medium text-[var(--state-bad)]',
    cancelButton:
      'inline-flex items-center rounded-[var(--radius-sm)] border border-[var(--line-strong)] bg-[var(--surface)] px-3 py-2 text-sm font-medium text-[var(--ink)] hover:bg-[var(--surface-hover)]',
  },
};

export interface OnayIstegi {
  baslik: string;
  /** Ne olacağını AÇIKÇA yazar — "Emin misiniz?" tek başına yeterli değildir. */
  mesaj: string;
  onayMetni: string;
  vazgecMetni?: string;
  /** Geri alınamaz işlem: düğme tehlike görünümüne geçer. */
  tehlikeli?: boolean;
}

/** Kullanıcı onayladı mı? Geri alınamaz her işlemden önce çağrılır. */
export async function onayIste(istek: OnayIstegi): Promise<boolean> {
  const sonuc = await Swal.fire({
    ...ORTAK,
    title: istek.baslik,
    text: istek.mesaj,
    icon: istek.tehlikeli ? 'warning' : 'question',
    showCancelButton: true,
    confirmButtonText: istek.onayMetni,
    cancelButtonText: istek.vazgecMetni ?? 'Vazgeç',
    // Odak "Vazgeç"te başlar: Enter'a basmak işlemi başlatmasın.
    focusCancel: true,
    reverseButtons: true,
    // Geri alınamaz işlemde onay düğmesi tehlike görünümüne geçer.
    customClass: istek.tehlikeli
      ? { ...ORTAK.customClass, confirmButton: TEHLIKE_DUGMESI }
      : ORTAK.customClass,
  });
  return sonuc.isConfirmed;
}

/** Köşede kısa bildirim — akışı kesmez. */
const kisaBildirim = Swal.mixin({
  ...ORTAK,
  toast: true,
  position: 'bottom-end',
  showConfirmButton: false,
  timer: 5000,
  timerProgressBar: true,
  // Fare üstüne gelince durur: uzun mesajı okumaya zaman kalsın.
  didOpen: (element) => {
    element.addEventListener('mouseenter', Swal.stopTimer);
    element.addEventListener('mouseleave', Swal.resumeTimer);
  },
});

export function basariBildir(mesaj: string, baslik = 'Tamamlandı'): void {
  void kisaBildirim.fire({ icon: 'success', title: baslik, text: mesaj });
}

export function bilgiBildir(mesaj: string, baslik = 'Bilgi'): void {
  void kisaBildirim.fire({ icon: 'info', title: baslik, text: mesaj });
}

/**
 * Hata bildirimi.
 *
 * Kısa bildirim yalnızca geçici/önemsiz hatalar için. Kullanıcının bir adım
 * atması gerekiyorsa (`yapilacak` dolu) ekranı kesen pencere gösterilir —
 * köşede kaybolan bir mesaj o adımı taşıyamaz.
 */
export function hataBildir(hata: unknown): void {
  const apiHatasi = hata instanceof ApiHatasi ? hata : null;
  const baslik = apiHatasi?.baslik ?? 'İşlem tamamlanamadı';
  const mesaj =
    apiHatasi?.message ?? (hata instanceof Error ? hata.message : 'Beklenmeyen bir sorun oluştu.');

  if (apiHatasi?.yapilacak) {
    void Swal.fire({
      ...ORTAK,
      icon: 'error',
      title: baslik,
      html: `<p>${kacisliMetin(mesaj)}</p><p class="mt-3 font-medium text-[var(--ink)]">Yapmanız gereken: ${kacisliMetin(apiHatasi.yapilacak)}</p>`,
      confirmButtonText: 'Anladım',
    });
    return;
  }

  void kisaBildirim.fire({ icon: 'error', title: baslik, text: mesaj });
}

/**
 * HTML'e gömülen metni kaçışlar.
 * SweetAlert `html` alanını ham HTML olarak basar; sunucudan gelen metinde
 * `<` geçerse sayfa bozulur (ve XSS yolu açılır).
 */
function kacisliMetin(metin: string): string {
  return metin.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Üretilen şifreyi **bir kez** gösterir.
 *
 * Neden köşe bildirimi değil de ekranı kesen pencere: bu şifre bir daha
 * gösterilemez (veritabanında yalnızca scrypt özeti var). Beş saniye sonra
 * kaybolan bir bildirim, kullanıcının şifreyi kaçırmasına ve hesabın
 * kullanılamaz hâle gelmesine yol açardı.
 *
 * Pencere kapanana kadar bekler — çağıran, kullanıcının gördüğünden emin olur.
 */
export async function sifreGoster(eposta: string, sifre: string, mesaj: string): Promise<void> {
  await Swal.fire({
    ...ORTAK,
    icon: 'success',
    title: 'Şifre oluşturuldu',
    html: `
      <p>${kacisliMetin(mesaj)}</p>
      <p class="mt-3 text-xs text-[var(--ink-faint)]">${kacisliMetin(eposta)}</p>
      <code class="mt-2 block select-all rounded-[var(--radius-sm)] border border-[var(--line-strong)] bg-[var(--surface-sunken)] px-3 py-2 text-base tracking-wider" style="font-family: var(--font-mono)">${kacisliMetin(sifre)}</code>
    `,
    confirmButtonText: 'Kaydettim, kapat',
    // Yanlışlıkla dışarı tıklayıp şifreyi kaçırmak olmasın.
    allowOutsideClick: false,
    allowEscapeKey: false,
  });
}
